import Urn from '@adc/structures/urn'
import { ChipColor, coreConstants } from '@adc/ui-morpheus'
import * as graph from '@adc/sdk'
import type {
  ShallowTaskWorkflowItemStatus,
  ShallowTaskWorkflowType,
} from '@adc/sdk/types'
import type {
  WorkflowAssignmentGroup,
  TaskAssignment,
} from '@adc/blueprint/lib/tasksAssignments/types'

const { ColorNames } = coreConstants
type ColorName = (typeof ColorNames)[keyof typeof ColorNames]

// ── Workflow resource labeling ──────────────────────────────────

/**
 * Truncates a full formatted address to "street, city, ST".
 * Input:  "510 10th St, Kemah, TX 77565, Galveston County"
 * Output: "510 10th St, Kemah, TX"
 */
export function formatCardAddress(
  address: string | null | undefined
): string | null {
  if (!address) return null
  const match = address.match(/^(.+?,\s*[A-Z]{2})\b/)
  return match ? match[1] : address
}

/**
 * Extracts the id portion from a URN ("adc:lead:12345" -> "12345").
 * Falls back to the whole string when it isn't a 3-part URN.
 */
export function idFromUrn(urn: string): string {
  return Urn.safelyParse(urn)?.identifier ?? urn
}

/**
 * Composite selection key for a workflow. A single resource_urn can back
 * multiple workflow entries (one per domain), so selection identity must
 * include the workflow_type to disambiguate them.
 */
export function workflowKey(
  resourceUrn: string,
  workflowType: string | null | undefined
): string {
  return `${resourceUrn}::${workflowType ?? 'UNKNOWN'}`
}

/**
 * Builds the workflow heading shown in the nav items and middle panel.
 * Listing-backed workflows read "Listing: {truncated address}"; everything
 * else (currently leads) reads "Lead: {id}". Falls back to the raw urn if the
 * type prefix is unrecognized.
 */
export function formatWorkflowLabel(
  listing:
    | { formatted_address?: Array<string | null> | null }
    | null
    | undefined,
  resourceUrn: string,
  fullAddress = false,
  associatedListingUrn?: string | null
): string {
  return formatWorkflowLabelFromParts(
    listing?.formatted_address?.[0] ?? null,
    resourceUrn,
    fullAddress,
    associatedListingUrn
  )
}

/**
 * Finds a listing URN among a transaction's associated URNs, if any. Used as a
 * fallback identifier when the transaction search index didn't resolve a
 * listing address for the workflow's resource.
 */
export function findAssociatedListingUrn(
  transaction:
    | { associated_urns?: Array<{ associated_urn: string }> | null }
    | null
    | undefined
): string | null {
  const urns = transaction?.associated_urns ?? []
  const listing = urns.find(u => u.associated_urn.startsWith('adc:listing:'))
  return listing?.associated_urn ?? null
}

/**
 * Bare context label for cross-workflow task rows (urgency / task-name modes) —
 * the address alone, no "Listing:" / "Lead:" prefix. Same fallback order as
 * formatWorkflowLabelFromParts, minus the type prefixes.
 */
export function workflowContextLabel(
  rawAddress: string | null | undefined,
  resourceUrn: string,
  associatedListingUrn?: string | null
): string {
  // Resolved street address always wins.
  if (rawAddress) return formatCardAddress(rawAddress) ?? rawAddress
  // Otherwise identify by the workflow's OWN resource, not a (possibly
  // dangling) associated URN. The associated listing is only a last resort
  // when the resource_urn itself is opaque.
  if (resourceUrn.startsWith('adc:listing:'))
    return `Listing ${idFromUrn(resourceUrn)}`
  if (resourceUrn.startsWith('adc:lead:'))
    return `Lead ${idFromUrn(resourceUrn)}`
  if (resourceUrn.startsWith('adc:transaction:'))
    return `Transaction ${idFromUrn(resourceUrn)}`
  if (associatedListingUrn) return `Listing ${idFromUrn(associatedListingUrn)}`
  return resourceUrn
}

/**
 * Same labeling as formatWorkflowLabel, but from a raw address string + urn
 * (used by the flat By Task rows, which carry only the address string).
 *
 * Fallback order when no listing address resolved:
 *   1. resource_urn identity   -> "Listing: 55102" / "Lead: 8801" / "Transaction: ..."
 *   2. associated listing URN  -> "Listing 2418881"
 *   3. anything else           -> the raw URN
 */
export function formatWorkflowLabelFromParts(
  rawAddress: string | null | undefined,
  resourceUrn: string,
  fullAddress = false,
  associatedListingUrn?: string | null
): string {
  if (rawAddress) {
    const addr = fullAddress ? rawAddress : formatCardAddress(rawAddress)
    return `Listing: ${addr ?? rawAddress}`
  }
  // Identify by the workflow's OWN resource before a (possibly dangling)
  // associated URN.
  if (resourceUrn.startsWith('adc:listing:')) {
    return `Listing: ${idFromUrn(resourceUrn)}`
  }
  if (resourceUrn.startsWith('adc:lead:')) {
    return `Lead: ${idFromUrn(resourceUrn)}`
  }
  if (resourceUrn.startsWith('adc:transaction:')) {
    return `Transaction: ${idFromUrn(resourceUrn)}`
  }
  if (associatedListingUrn) {
    return `Listing: ${idFromUrn(associatedListingUrn)}`
  }
  return resourceUrn
}

/**
 * Left-panel group-by modes. The left nav shows group rows; selecting a group
 * loads its tasks into the middle panel.
 *   urgency  — group open tasks by due-date band (Overdue, Due Today, ...)
 *   workflow — group by parent workflow (property / lead)
 *   taskName — group tasks that share the same label
 */
export type ViewMode = 'urgency' | 'workflow' | 'taskName'

/** Group-by selector order + labels/icons for the toggle. */
export const VIEW_MODE_ORDER: readonly ViewMode[] = [
  'urgency',
  'workflow',
  'taskName',
] as const

export const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  urgency: 'Urgency',
  workflow: 'Workflow',
  taskName: 'Task Name',
}

export const VIEW_MODE_ICONS: Record<ViewMode, string> = {
  urgency: 'fire',
  workflow: 'folder',
  taskName: 'list-check',
}

/**
 * Normalizes a raw view-mode string from the URL. Migrates the legacy 'task'
 * value (flat by-task list) to the new 'taskName' grouping.
 */
export function normalizeViewMode(
  raw: string | null | undefined
): ViewMode | undefined {
  if (raw === 'workflow' || raw === 'urgency' || raw === 'taskName') return raw
  if (raw === 'task') return 'taskName'
  return undefined
}

/**
 * Workflow domains. Mirrors ShallowTaskWorkflowType from the graph
 * (CLIENT_ONBOARDING intentionally omitted — not surfaced in the work queue).
 */
export type WorkflowDomain =
  | 'INTAKE'
  | 'MARKETPLACE_ACTIVATION'
  | 'SELLING'
  | 'CLOSING'

/** Domain filter value — 'ALL' plus each concrete domain. */
export type DomainFilter = 'ALL' | WorkflowDomain

/** Funnel order — earliest stage first. Used to sort nav items and filter chips. */
export const DOMAIN_ORDER: readonly WorkflowDomain[] = [
  'INTAKE',
  'MARKETPLACE_ACTIVATION',
  'SELLING',
  'CLOSING',
] as const

/** Human-readable labels per domain. */
export const DOMAIN_LABELS: Record<WorkflowDomain, string> = {
  INTAKE: 'Intake',
  MARKETPLACE_ACTIVATION: 'Marketplace Activation',
  SELLING: 'Selling',
  CLOSING: 'Closing',
}

/** Compact labels for space-constrained UI (e.g. the Type filter chips). */
export const DOMAIN_SHORT_LABELS: Record<WorkflowDomain, string> = {
  INTAKE: 'Intake',
  MARKETPLACE_ACTIVATION: 'Mkt Activation',
  SELLING: 'Selling',
  CLOSING: 'Closing',
}

/** FontAwesome icon names per domain (matches transactionWorkflowPanel conventions). */
export const DOMAIN_ICONS: Record<WorkflowDomain, string> = {
  INTAKE: 'arrow-right-to-bracket',
  MARKETPLACE_ACTIVATION: 'store',
  SELLING: 'gavel',
  CLOSING: 'handshake',
}

/** Chip color per domain for the domain badge on nav items. */
export const DOMAIN_CHIP_COLORS: Record<WorkflowDomain, ChipColor> = {
  INTAKE: ChipColor.Info,
  MARKETPLACE_ACTIVATION: ChipColor.Success,
  SELLING: ChipColor.Caution,
  CLOSING: ChipColor.Purple,
}

/**
 * InfoPill bg/fg color names per domain.
 * bg = light tint, fg = dark shade — matches Chip 'light' selectionStyle visually
 * but without a border.
 */
export const DOMAIN_PILL_COLORS: Record<
  WorkflowDomain,
  { bg: ColorName; fg: ColorName }
> = {
  INTAKE: { bg: ColorNames.InfoExtraLight, fg: ColorNames.InfoDark },
  MARKETPLACE_ACTIVATION: {
    bg: ColorNames.SuccessExtraLight,
    fg: ColorNames.SuccessDark,
  },
  SELLING: { bg: ColorNames.CautionExtraLight, fg: ColorNames.CautionDark },
  CLOSING: { bg: ColorNames.ErrorExtraLight, fg: ColorNames.ErrorDark },
}

/**
 * Narrows an arbitrary workflow_type string to a known WorkflowDomain,
 * or null if it isn't one we render (e.g. CLIENT_ONBOARDING / UNKNOWN).
 */
export function toWorkflowDomain(
  workflowType: string | null | undefined
): WorkflowDomain | null {
  if (!workflowType) return null
  return DOMAIN_ORDER.includes(workflowType as WorkflowDomain)
    ? (workflowType as WorkflowDomain)
    : null
}

/**
 * Counts how many of a workflow's assignments belong to the current user.
 */
export function countMineVsOthers(
  assignments: readonly TaskAssignment[],
  currentPartyId: string | null
): { mine: number; others: number } {
  if (!currentPartyId) {
    return { mine: 0, others: assignments.length }
  }
  const mine = assignments.filter(a =>
    a.assignee_urn.includes(currentPartyId)
  ).length
  return { mine, others: assignments.length - mine }
}

/**
 * True when a task assignment is considered "done".
 *
 * NOTE: the backend reports task_status as COMPLETE for finished tasks, but
 * work_queue.completed_tasks currently miscounts (see MOR-90 / MOR-138). We derive
 * counts from assignment statuses here so the UI is accurate until that lands.
 */
export function isAssignmentComplete(assignment: TaskAssignment): boolean {
  return (
    (assignment.task_status as string) ===
    graph.enums.ShallowTaskWorkflowItemStatus.Complete
  )
}

/** True when a task assignment is actively in progress. */
export function isAssignmentOngoing(assignment: TaskAssignment): boolean {
  return (
    (assignment.task_status as string) ===
    graph.enums.ShallowTaskWorkflowItemStatus.Ongoing
  )
}

/**
 * True when a task is "impromptu" — manually entered by a user rather than
 * generated by the workflow system. Detected by the target_task_urn prefix
 * (e.g. "adc:impromptu_task:6").
 */
export function isImpromptuTask(assignment: TaskAssignment): boolean {
  return assignment.target_task_urn?.startsWith('adc:impromptu_task:') ?? false
}

/**
 * Reads the assignee's display name off a task assignment. The work queue
 * query selects `display_name` on every Actor variant, but the shared Actor
 * union type doesn't surface it — so we narrow structurally here.
 */
export function getAssigneeName(assignment: TaskAssignment): string | null {
  const assignee = assignment.assignee as {
    display_name?: string | null
  } | null
  return assignee?.display_name ?? null
}

/**
 * Derives up-to-two-letter initials from a display name.
 * "Jane Doe" -> "JD", "Cher" -> "C", null -> "?".
 */
export function initialsFromName(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Derives completed/total task counts for a workflow from its assignments.
 *
 * Counts are based on assignments (not backend completed_tasks/total_tasks),
 * so the progress fraction matches the mine/others line on the nav item and
 * the aggregate header summary. Backend total_tasks includes unassigned tasks
 * and miscounts completions (MOR-138), so it's intentionally not used here.
 */
export function deriveProgress(group: WorkflowAssignmentGroup): {
  completed: number
  total: number
} {
  const assignments = group.assignments ?? []
  return {
    completed: assignments.filter(isAssignmentComplete).length,
    total: assignments.length,
  }
}

// ── By Task view ─────────────────────────────────────────────────────

/**
 * Coarse task status buckets for the flat "By Task" nav grouping.
 * NOTE: urgency (overdue/due-today) isn't available yet (MOR-90), so we
 * group by status instead. Order is display order: active work first.
 */
export type TaskStatusGroup = `${ShallowTaskWorkflowItemStatus}`

export const TASK_STATUS_GROUP_ORDER: readonly TaskStatusGroup[] = [
  graph.enums.ShallowTaskWorkflowItemStatus.Ongoing,
  graph.enums.ShallowTaskWorkflowItemStatus.Upcoming,
  graph.enums.ShallowTaskWorkflowItemStatus.Complete,
] as const

export const TASK_STATUS_GROUP_LABELS: Record<TaskStatusGroup, string> = {
  ONGOING: 'In Progress',
  UPCOMING: 'Not Started',
  COMPLETE: 'Completed',
}

/** FontAwesome icon per status group for the section header. */
export const TASK_STATUS_GROUP_ICONS: Record<TaskStatusGroup, string> = {
  ONGOING: 'spinner',
  UPCOMING: 'play-pause',
  COMPLETE: 'check',
}

/** Chip color per status group (task row status badge + section accent). */
export const TASK_STATUS_GROUP_COLORS: Record<TaskStatusGroup, ChipColor> = {
  ONGOING: ChipColor.Info,
  UPCOMING: ChipColor.Gray,
  COMPLETE: ChipColor.Success,
}

/**
 * Deprecated legacy task_status codes and the current bucket they map to.
 *
 * The backend's foundation TaskStatus enum keeps deprecated aliases for old DB
 * rows: PENDING is a legacy spelling of UPCOMING. The service echoes these
 * verbatim, so we normalize them here explicitly rather than relying on the
 * unknown-status fallthrough.
 */
const LEGACY_STATUS_ALIASES: Record<string, TaskStatusGroup> = {
  PENDING: graph.enums.ShallowTaskWorkflowItemStatus.Upcoming,
}

/** Maps a raw task_status to one of the three display buckets. */
export function toTaskStatusGroup(
  status: string | null | undefined
): TaskStatusGroup {
  if (!status) return graph.enums.ShallowTaskWorkflowItemStatus.Upcoming
  const alias = LEGACY_STATUS_ALIASES[status]
  if (alias) return alias
  return TASK_STATUS_GROUP_ORDER.includes(status as TaskStatusGroup)
    ? (status as TaskStatusGroup)
    : graph.enums.ShallowTaskWorkflowItemStatus.Upcoming
}

/**
 * A single assignment flattened with its parent workflow context, so the
 * "By Task" view can render a task row without re-deriving the workflow.
 */
export interface FlatTask {
  assignment: TaskAssignment
  resourceUrn: string
  workflowType: string | null
  domain: WorkflowDomain | null
  address: string | null
  sellerName: string | null
  /** Fallback listing URN from the transaction, when no address resolved. */
  associatedListingUrn: string | null
}

/**
 * Flattens all assignments across workflows into a single task list, each
 * carrying its parent workflow context.
 */
export function flattenTasks(
  workflows: readonly WorkflowAssignmentGroup[]
): FlatTask[] {
  const tasks: FlatTask[] = []
  for (const wf of workflows) {
    const domain = toWorkflowDomain(wf.workflow_type)
    const address = wf.transaction?.listing?.formatted_address?.[0] ?? null
    const associatedListingUrn = findAssociatedListingUrn(wf.transaction)
    const sellerName =
      wf.transaction?.parties?.find(p => p.party_role === 'PRIVATE_SELLER')
        ?.party_name ?? null
    for (const assignment of wf.assignments ?? []) {
      tasks.push({
        assignment,
        resourceUrn: wf.resource_urn,
        workflowType: wf.workflow_type,
        domain,
        address,
        sellerName,
        associatedListingUrn,
      })
    }
  }
  return tasks
}

/**
 * Groups flat tasks into status buckets (In Progress → Not Started → Completed).
 * Within a bucket, sorts by funnel domain order then address.
 */
export function groupTasksByStatus(
  tasks: readonly FlatTask[]
): { group: TaskStatusGroup; tasks: FlatTask[] }[] {
  const buckets = new Map<TaskStatusGroup, FlatTask[]>()
  for (const group of TASK_STATUS_GROUP_ORDER) buckets.set(group, [])
  for (const task of tasks) {
    buckets.get(toTaskStatusGroup(task.assignment.task_status))!.push(task)
  }
  const sortWithin = (a: FlatTask, b: FlatTask): number => {
    const aIdx = a.domain ? DOMAIN_ORDER.indexOf(a.domain) : 999
    const bIdx = b.domain ? DOMAIN_ORDER.indexOf(b.domain) : 999
    if (aIdx !== bIdx) return aIdx - bIdx
    return (a.address ?? a.resourceUrn).localeCompare(
      b.address ?? b.resourceUrn
    )
  }
  return TASK_STATUS_GROUP_ORDER.map(group => ({
    group,
    tasks: buckets.get(group)!.slice().sort(sortWithin),
  })).filter(section => section.tasks.length > 0)
}

// ── Due dates & urgency ──────────────────────────────────────────────

/**
 * Fine-grained due buckets for the "By Task" grouping (when Completed is off).
 * Order below is display order: most urgent first.
 */
export type DueCategory =
  | 'OVERDUE'
  | 'DUE_TODAY'
  | 'DUE_SOON'
  | 'FUTURE'
  | 'NO_DUE_DATE'
  | 'COMPLETED'

export const DUE_CATEGORY_ORDER: readonly DueCategory[] = [
  'OVERDUE',
  'DUE_TODAY',
  'DUE_SOON',
  'FUTURE',
  'NO_DUE_DATE',
  'COMPLETED',
] as const

export const DUE_CATEGORY_LABELS: Record<DueCategory, string> = {
  OVERDUE: 'Overdue',
  DUE_TODAY: 'Due Today',
  DUE_SOON: 'Due Soon',
  FUTURE: 'Upcoming',
  NO_DUE_DATE: 'No Due Date',
  COMPLETED: 'Completed',
}

export const DUE_CATEGORY_ICONS: Record<DueCategory, string> = {
  OVERDUE: 'circle-exclamation',
  DUE_TODAY: 'fire',
  DUE_SOON: 'clock',
  FUTURE: 'calendar-week',
  NO_DUE_DATE: 'calendar',
  COMPLETED: 'circle-check',
}

/** Colors per urgency band — drives inline icon/label styling. */
export const DUE_CATEGORY_COLORS: Record<DueCategory, string> = {
  OVERDUE: coreConstants.colorVars.utkColorErrorDark,
  DUE_TODAY: coreConstants.colorVars.utkColorCautionDark,
  DUE_SOON: coreConstants.colorVars.utkColorCautionDark,
  FUTURE: coreConstants.colorVars.utkColorAction,
  NO_DUE_DATE: coreConstants.colorVars.utkColorGray,
  COMPLETED: coreConstants.colorVars.utkColorSuccessDark,
}

/**
 * Coarse buckets used by the header urgency filter chips. Completed tasks are
 * governed by the Completed toggle, not urgency, so they're excluded here.
 */
export type UrgencyFilter = 'OVERDUE' | 'DUE_TODAY' | 'REMAINING'

export const URGENCY_FILTER_ORDER: readonly UrgencyFilter[] = [
  'OVERDUE',
  'DUE_TODAY',
  'REMAINING',
] as const

/**
 * Age filter — restricts the work_queue fetch window via fromDate. Single-select,
 * one value always required. Maps to a number of days back from now.
 */
export type AgeFilter = 'TWO_WEEKS' | 'ONE_MONTH' | 'TWO_MONTHS'

export const AGE_FILTER_ORDER: readonly AgeFilter[] = [
  'TWO_WEEKS',
  'ONE_MONTH',
  'TWO_MONTHS',
] as const

export const AGE_FILTER_LABELS: Record<AgeFilter, string> = {
  TWO_WEEKS: 'Past 2 Weeks',
  ONE_MONTH: 'Past Month',
  TWO_MONTHS: 'Past 2 Months',
}

export const AGE_FILTER_DAYS: Record<AgeFilter, number> = {
  TWO_WEEKS: 14,
  ONE_MONTH: 30,
  TWO_MONTHS: 60,
}

export const DEFAULT_AGE_FILTER: AgeFilter | null = 'ONE_MONTH'

/** Converts an Age filter to an ISO datetime fromDate string (N days ago, start of day). */
export function ageFilterToFromDate(age: AgeFilter): string {
  const d = new Date()
  d.setDate(d.getDate() - AGE_FILTER_DAYS[age])
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

/** Default sets for filter initialization — all options selected. */
export const ALL_DOMAINS: ReadonlySet<WorkflowDomain> = new Set(DOMAIN_ORDER)
export const ALL_URGENCY: ReadonlySet<UrgencyFilter> = new Set(
  URGENCY_FILTER_ORDER
)
export const DEFAULT_STATUS: ReadonlySet<TaskStatusGroup> = new Set([
  'ONGOING',
  'UPCOMING',
] as TaskStatusGroup[])

// ── Server-side filter derivation (Phase 1a) ─────────────────────────────────

/**
 * Server-side params the work_queue query can push down to the backend.
 * The BE supports a single workflow_type and an overdue-only flag; anything
 * requiring multi-select (2+ domains) or finer urgency stays client-side.
 */
export interface WorkQueueServerFilters {
  /** Single domain to filter server-side, or undefined to fetch all domains. */
  workflowType?: ShallowTaskWorkflowType
}

/**
 * Derives the server-pushable subset of the active filter state.
 *
 * Type: `workflowType` only when EXACTLY ONE domain is selected — the BE param
 * is single-valued, so 0 (all) or 2+ (multi-select) domains are left to the
 * client filter and no `workflowType` is sent.
 *
 * Urgency: `overdueOnly` only when OVERDUE is the sole active urgency chip.
 * Any broader urgency selection (or none) stays client-side, since the BE flag
 * is all-or-nothing overdue.
 *
 * NOTE: `overdueOnly` is intentionally NOT derived here. The backend's overdue
 * state (`is_overdue` / `overdueDate`) is stamped asynchronously by a scheduled
 * OverdueSweepService, so it is unreliable in non-prod (and lags in prod) versus
 * the client's live per-assignment derivation from `expected_completion_date`.
 * Pushing `overdueOnly` server-side returned zero groups whenever the sweep
 * hadn't run. Overdue filtering therefore stays fully client-side; only
 * `workflowType` is pushed down.
 *
 * The client-side filter pipeline still runs over whatever the server returns,
 * so these are pure narrowing optimizations — never the source of truth.
 */
export function deriveServerFilters(
  typeFilter: ReadonlySet<WorkflowDomain>
): WorkQueueServerFilters {
  const workflowType =
    typeFilter.size === 1
      ? // WorkflowDomain values are a subset of ShallowTaskWorkflowType, so the
        // single selected domain is a valid enum value.
        ([...typeFilter][0] as unknown as ShallowTaskWorkflowType)
      : undefined
  return { workflowType }
}

/** How many calendar days out still counts as "due soon". */
const DUE_SOON_DAYS = 3

/**
 * Returns local midnight for a date (strips time-of-day), so day-boundary
 * comparisons don't get skewed by hours/minutes.
 */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/**
 * Whole-day delta between the task's due date and today.
 * Negative = overdue, 0 = due today, positive = days remaining.
 * Returns null when there's no due date.
 */
export function daysUntilDue(assignment: TaskAssignment): number | null {
  const raw = assignment.task?.expected_completion_date
  if (!raw) return null
  const due = new Date(raw)
  if (Number.isNaN(due.getTime())) return null
  const today = startOfDay(new Date())
  const dueDay = startOfDay(due)
  return Math.round((dueDay.getTime() - today.getTime()) / 86_400_000)
}

/**
 * Maps a task assignment to a fine-grained due category. Completed tasks
 * return null (they're not bucketed by due date). Uses expected_completion_date.
 */
export function toDueCategory(assignment: TaskAssignment): DueCategory {
  if (isAssignmentComplete(assignment)) return 'COMPLETED'
  const days = daysUntilDue(assignment)
  if (days === null) return 'NO_DUE_DATE'
  if (days < 0) return 'OVERDUE'
  if (days === 0) return 'DUE_TODAY'
  if (days <= DUE_SOON_DAYS) return 'DUE_SOON'
  return 'FUTURE'
}

/**
 * Maps a task assignment to a coarse urgency-filter bucket. Completed tasks
 * return null. Everything open that isn't overdue or due-today is REMAINING.
 */
export function toUrgencyFilter(
  assignment: TaskAssignment
): UrgencyFilter | null {
  const cat = toDueCategory(assignment)
  if (cat === 'COMPLETED') return null
  if (cat === 'OVERDUE') return 'OVERDUE'
  if (cat === 'DUE_TODAY') return 'DUE_TODAY'
  return 'REMAINING'
}

/**
 * Short human-readable due label for a task row badge.
 * "2d overdue", "Due today", "Due in 3d", or null (no due date / completed).
 */
export function dueBadgeText(assignment: TaskAssignment): string | null {
  if (isAssignmentComplete(assignment)) return null
  const days = daysUntilDue(assignment)
  if (days === null) return null
  if (days < 0) {
    const n = Math.abs(days)
    return `${n}d overdue`
  }
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  return `Due in ${days}d`
}

/** Count of open, overdue tasks in a workflow. */
export function countOverdue(group: WorkflowAssignmentGroup): number {
  return (group.assignments ?? []).filter(a => toDueCategory(a) === 'OVERDUE')
    .length
}

/**
 * Whether a workflow group should show an overdue indicator.
 *
 * Uses live client-side per-assignment derivation (any open task past its
 * expected_completion_date), falling back to the backend `is_overdue` flag.
 * Client derivation is primary because the backend flag is stamped by an async
 * sweep and lags/absent in non-prod.
 */
export function isGroupOverdue(group: WorkflowAssignmentGroup): boolean {
  return countOverdue(group) > 0 || group.is_overdue
}

/**
 * Overdue badge label for a workflow group. Shows a count when client-side
 * date derivation has one ("3 overdue"); otherwise a plain "Overdue" when only
 * the backend flag is set (no per-task due dates to count).
 */
export function overdueBadgeLabel(group: WorkflowAssignmentGroup): string {
  const count = countOverdue(group)
  return count > 0 ? `${count} overdue` : 'Overdue'
}

/** Count of open assignments due tomorrow (daysUntilDue === 1). */
export function countDueTomorrow(group: WorkflowAssignmentGroup): number {
  return (group.assignments ?? []).filter(
    a => !isAssignmentComplete(a) && daysUntilDue(a) === 1
  ).length
}

/**
 * Combined urgency summary for a workflow group's header. Joins overdue and
 * due-tomorrow counts, e.g. "2 overdue, 4 due tomorrow". Omits a segment when
 * its count is 0; returns null when both are 0.
 */
export function workflowUrgencySummary(
  group: WorkflowAssignmentGroup
): string | null {
  const parts: string[] = []
  const overdue = countOverdue(group)
  const tomorrow = countDueTomorrow(group)
  if (overdue > 0) parts.push(`${overdue} overdue`)
  else if (group.is_overdue) parts.push('Overdue')
  if (tomorrow > 0) parts.push(`${tomorrow} due tomorrow`)
  return parts.length > 0 ? parts.join(', ') : null
}

/**
 * Groups flat tasks into due-date buckets (Overdue → Due Today → Due Soon →
 * Upcoming → No Due Date). Completed tasks are dropped (bucket only open work).
 * Within a bucket, sorts by soonest due date, then domain order, then address.
 */
export function groupTasksByDue(
  tasks: readonly FlatTask[]
): { group: DueCategory; tasks: FlatTask[] }[] {
  const buckets = new Map<DueCategory, FlatTask[]>()
  for (const group of DUE_CATEGORY_ORDER) buckets.set(group, [])
  for (const task of tasks) {
    const cat = toDueCategory(task.assignment)
    buckets.get(cat)!.push(task)
  }
  const sortWithin = (a: FlatTask, b: FlatTask): number => {
    const aDays = daysUntilDue(a.assignment)
    const bDays = daysUntilDue(b.assignment)
    // Both dated: soonest first. Dated before undated.
    if (aDays !== null && bDays !== null && aDays !== bDays)
      return aDays - bDays
    if (aDays !== null && bDays === null) return -1
    if (aDays === null && bDays !== null) return 1
    const aIdx = a.domain ? DOMAIN_ORDER.indexOf(a.domain) : 999
    const bIdx = b.domain ? DOMAIN_ORDER.indexOf(b.domain) : 999
    if (aIdx !== bIdx) return aIdx - bIdx
    return (a.address ?? a.resourceUrn).localeCompare(
      b.address ?? b.resourceUrn
    )
  }
  return DUE_CATEGORY_ORDER.map(group => ({
    group,
    tasks: buckets.get(group)!.slice().sort(sortWithin),
  })).filter(section => section.tasks.length > 0)
}

// ── Group-driven left nav (all 3 view modes) ──────────────────────────

/**
 * A resolved left-nav group. Selecting it drives the middle panel. Carries
 * enough presentation metadata to render a group row without re-deriving.
 */
export interface TaskGroup {
  /** Stable identity for selection + URL persistence. */
  key: string
  /** Display label for the group row + middle header. */
  label: string
  /** FontAwesome icon (urgency bands + task-name rows). */
  icon?: string
  /**
   * CSS accent key for the left border. For urgency bands this is
   * the due-category css key (e.g. 'overdue'); undefined for other modes.
   */
  accent?: string
  /** Inline hex color for icon + label in urgency mode. */
  color?: string
  /** For workflow groups, the underlying workflow (drives the middle header). */
  workflow?: WorkflowAssignmentGroup
  /** Flattened tasks in this group. */
  tasks: FlatTask[]
}

/** CSS accent key per urgency band — matches the due-badge-* / due-* classes. */
function dueCategoryAccent(cat: DueCategory): string {
  return cat.toLowerCase().replace(/_/g, '-')
}

/**
 * Builds the left-nav groups from a pre-filtered flat task set.
 *
 * urgency  — one group per non-empty due band (Overdue → ... → Completed),
 *            in DUE_CATEGORY_ORDER.
 * workflow — one group per workflow that still has visible tasks, sorted by the
 *            provided workflow order (caller passes already-sorted workflows).
 * taskName — one group per distinct task label, sorted by count desc then name.
 */
export function buildTaskGroups(
  tasks: readonly FlatTask[],
  mode: ViewMode,
  workflows: readonly WorkflowAssignmentGroup[]
): TaskGroup[] {
  if (mode === 'urgency') {
    const buckets = new Map<DueCategory, FlatTask[]>()
    for (const cat of DUE_CATEGORY_ORDER) buckets.set(cat, [])
    for (const t of tasks) buckets.get(toDueCategory(t.assignment))!.push(t)
    return DUE_CATEGORY_ORDER.map(cat => ({
      key: cat,
      label: DUE_CATEGORY_LABELS[cat],
      icon: DUE_CATEGORY_ICONS[cat],
      accent: dueCategoryAccent(cat),
      color: DUE_CATEGORY_COLORS[cat],
      tasks: buckets.get(cat)!,
    })).filter(g => g.tasks.length > 0)
  }

  if (mode === 'workflow') {
    const byKey = new Map<string, FlatTask[]>()
    for (const t of tasks) {
      const k = workflowKey(t.resourceUrn, t.workflowType)
      const arr = byKey.get(k) ?? []
      arr.push(t)
      byKey.set(k, arr)
    }
    const groups: TaskGroup[] = []
    for (const wf of workflows) {
      const k = workflowKey(wf.resource_urn, wf.workflow_type)
      const wfTasks = byKey.get(k)
      if (!wfTasks || wfTasks.length === 0) continue
      groups.push({
        key: k,
        label: formatWorkflowLabel(
          wf.transaction?.listing,
          wf.resource_urn,
          true,
          findAssociatedListingUrn(wf.transaction)
        ),
        workflow: wf,
        tasks: wfTasks,
      })
    }
    return groups
  }

  // taskName — group tasks sharing the same label. Sort by count desc, then A–Z.
  const byName = new Map<string, FlatTask[]>()
  for (const t of tasks) {
    const label = t.assignment.task_label ?? t.assignment.task_code ?? 'Task'
    const arr = byName.get(label) ?? []
    arr.push(t)
    byName.set(label, arr)
  }
  return [...byName.entries()]
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
    .map(([label, groupTasks]) => ({ key: label, label, tasks: groupTasks }))
}

// ── URL filter param ("f") ────────────────────────────────────────────────────

/**
 * Compact shape stored in the base64-encoded "f" URL param.
 * Keys are intentionally short to keep the encoded string small.
 */
export interface FilterParams {
  /** View mode: 'urgency' | 'workflow' | 'taskName' */
  v?: ViewMode
  /** Search text */
  s?: string
  /** Mine-only toggle: 1 = mine only, 0 = all */
  m?: 0 | 1
  /** Urgency filter items */
  u?: UrgencyFilter[]
  /** Status filter items */
  st?: TaskStatusGroup[]
  /** Age filter */
  a?: AgeFilter | null
  /** Type (domain) filter items */
  t?: WorkflowDomain[]
}

/**
 * Encodes the current filter state into a base64 string for the "f" URL param.
 */
export function encodeFilters(f: FilterParams): string {
  return btoa(JSON.stringify(f))
}

/**
 * Decodes the "f" URL param back to a FilterParams object.
 * Returns an empty object on any parse error so the page always loads cleanly.
 */
export function decodeFilters(encoded: string | null): FilterParams {
  if (!encoded) return {}
  try {
    const parsed = JSON.parse(atob(encoded))
    return typeof parsed === 'object' && parsed !== null ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * Contiguous range of keys between `a` and `b` (inclusive) in the given display
 * order. Order-agnostic to which of a/b comes first. Keys not present in
 * `order` are ignored. Used for shift-click range selection in the left nav.
 */
export function rangeOfKeys(
  order: readonly string[],
  a: string,
  b: string
): string[] {
  const ia = order.indexOf(a)
  const ib = order.indexOf(b)
  if (ia === -1 || ib === -1) return [b].filter(k => order.includes(k))
  const [lo, hi] = ia <= ib ? [ia, ib] : [ib, ia]
  return order.slice(lo, hi + 1)
}
